import React, {useEffect, useRef, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import ApiInterface from "@/commons/api/ApiInterface";
import Header from "@/commons/pages/borders/header/Header";
import Footer from "@/commons/pages/borders/footer/Footer";
import styles from "../logchat.module.css";

import { Virtuoso } from 'react-virtuoso';
import { debounce } from 'lodash'
import style from "@/admin/pages/logs/singlechat/logchat.module.css";
import {loadFileInfos} from "@/admin/pages/logs/singlechat/utils/SingleLogUtils";
import CustomButton from "@/commons/components/buttons/customButton/CustomButton";
import FloatingInput from "@/commons/components/fields/floatingInput/FloatingInput";

const SingleLogChatPrivate = () => {
    const { uuid } = useParams();
    const [logLines, setLogLines] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [nameSearch, setNameSearch] = useState("");
    const [startTime, setStartTime] = useState("00:00:00");
    const [endTime, setEndTime] = useState("23:59:59");
    const [fileName, setFileName] = useState("");
    const [publicFile, setPublicFile] = useState(null);

    const [prevFile, setPrevFile] = useState(null);
    const [nextFile, setNextFile] = useState(null);

    useEffect(() => {
        setLogLines([])
        loadFileInfos(uuid, setPrevFile, setNextFile, setFileName, setPublicFile, "/logs/private/", "/logs/public/");
        ApiInterface.getFileUrl(uuid).then((url) => {
            fetch(url)
                .then(res => res.text())
                .then(text => {
                    const lines = text.split("\n").filter(Boolean);
                    setLogLines(lines);
                });
        });
    }, [uuid]);

    const debouncedSearch = debounce((val) => {
        setSearchTerm(val.toLowerCase())
    }, 300);
    const handleSearch = (e) => debouncedSearch(e.target.value || "");

    const debouncedSearchName = debounce((val) => {
        setNameSearch(val.toLowerCase())
    }, 300);

    const handleNameSearch = (e) => debouncedSearchName(e.target.value || "");

    const getFilteredLines = () => {
        return logLines.filter(line => {
            const timeMatch = line.match(/^(\d{2}:\d{2}:\d{2})/);
            const time = timeMatch ? timeMatch[1] : null;
            if (!time || time < startTime || time > endTime) return false;

            const msgMatch = line.match(/^(\d{2}:\d{2}:\d{2}) \[(.+?) -> (.+?)\] (.+)$/);
            if (!msgMatch) return false;

            const [, , from, to, message] = msgMatch;

            if (nameSearch) {
                const low = nameSearch.toLowerCase();
                if (!from.toLowerCase().includes(low) && !to.toLowerCase().includes(low)) return false;
            }

            if (searchTerm && !message.toLowerCase().includes(searchTerm)) return false;

            return true; // passa tutti i filtri
        });
    };

    const highlight = (text, term) => {
        if (!term) return text;
        const parts = text.split(new RegExp(`(${term})`, 'gi'));
        return parts.map((part, i) =>
            part.toLowerCase() === term ? <mark key={i}>{part}</mark> : part
        );
    };
    const highlightName = (text) => {
        if (!nameSearch) return text;
        const parts = text.split(new RegExp(`(${nameSearch})`, 'gi'));
        return parts.map((part, i) =>
            part.toLowerCase() === nameSearch ? <mark className={styles.nameMark} key={i}>{part}</mark> : part
        );
    };

    const navigate = useNavigate();
    return (
        <>
            <div className="page__body">
                <Header/>
                <div className={"page__content"}>
                    <div className={styles.logNavbar}>
                        <CustomButton className={styles.button} onClick={() => window.location.href = "/logs/chats/private"}>
                            Vedi tutti i Log Privati
                        </CustomButton>
                        <CustomButton className={styles.button} onClick={() => window.location.href = "/logs/chat/public/"+publicFile}>
                            Passa al Log Pubblico
                        </CustomButton>
                    </div>
                    <div className={style.logNavbar}>
                        <CustomButton className={styles.inlineButton}>
                            {prevFile ? (
                                <CustomButton className={styles.inlineClickableButton} onClick={() => navigate("/logs/chat/private/"+prevFile)}>
                                    «
                                </CustomButton>
                            ) : (
                                <CustomButton className={styles.inlineNotClickableButton}>&nbsp;</CustomButton>
                            )}
                            Log del {fileName ? (<>{fileName}</>) : (<>XX.XX.XXXX</>)}
                            {nextFile ? (
                                <CustomButton className={styles.inlineClickableButton} onClick={() => navigate("/logs/chat/private/"+nextFile)}>
                                    »
                                </CustomButton>
                            ) : (
                                <CustomButton className={styles.inlineNotClickableButton}>&nbsp;</CustomButton>
                            )}
                        </CustomButton>
                    </div>
                    <div className={styles.filtersBox}>
                        <div className={styles.timeFilters}>
                            <label>
                                Da:
                                <input
                                    type="time"
                                    value={startTime.slice(0, 5)}
                                    onChange={(e) => setStartTime(e.target.value + ":00")}
                                />
                            </label>
                            <label>
                                A:
                                <input
                                    type="time"
                                    value={endTime.slice(0, 5)}
                                    onChange={(e) => setEndTime(e.target.value + ":00")}
                                />
                            </label>
                        </div>

                        <FloatingInput
                            type="text"
                            className={styles.searchInput}
                            label="Filtra per nome (da o a)..."
                            onChange={handleNameSearch}
                        />

                        <FloatingInput
                            type="text"
                            className={styles.searchInput}
                            label="Cerca nel messaggio..."
                            onChange={handleSearch}
                        />
                    </div>

                    <div className={styles.logContainer} style={{ height: "600px" }}>
                        <Virtuoso
                            className={styles.virtuosoScroller}
                            style={{ height: "100%", width: "100%" }}
                            totalCount={getFilteredLines().length}
                            itemContent={(index) => {
                                const line = getFilteredLines()[index];
                                const match = line.match(/^(\d{2}:\d{2}:\d{2}) \[(.+?) -> (.+?)\] (.+)$/);
                                if (!match) return null;

                                const [, time, from, to, message] = match;

                                return (
                                    <div className={styles.logLinePrivate}>
                                        <div className={styles.time}>{time}</div>
                                        <div className={styles.fromto}>
                                            <span>{highlightName(from)}</span>
                                            <span>↓ ↓</span>
                                            <span>{highlightName(to)}</span>
                                        </div>
                                        <div className={styles.text}>
                                            {highlight(message, searchTerm)}
                                        </div>
                                    </div>
                                );
                            }}
                        />
                    </div>

                </div>
                <Footer />
            </div>
        </>
    );
};

export default SingleLogChatPrivate;
